'use client';

import type { CSSProperties } from 'react';
import { BlockRoot, PortalLink, tx } from '@akademi/site-builder-runtime';

type Localized = string | Record<string, string>;

export interface SolarPromoTile {
  title: Localized;
  /** One short line. What the tile is actually offering, not a slogan. */
  body?: Localized;
  href: string;
  ctaLabel?: Localized;
  /** `brand` is the one red tile. Exactly one, or the grid stops having a primary. */
  tone?: 'brand' | 'dark' | 'plain';
  image?: string;
  imageAlt?: string;
}

export interface SolarPromoGridProps {
  /** The page's h1. Present for the document, small on screen — see below. */
  title?: Localized;
  tiles?: SolarPromoTile[];
}

/**
 * The top of the home page: a grid of promotional tiles.
 *
 * <h3>Why this replaced the hero</h3>
 * The reference has no hero. Its home page opens with a multi-tier header and then goes
 * straight into promo banners — Power Tool Deals, New Deals, Lighting Deals — followed by
 * category cards. There is no lifestyle photograph, no centred headline, no pair of
 * buttons over an image.
 *
 * <p>Ours had exactly that: a sunset photograph of a cabin, a kicker, a 52px headline, a
 * description and two calls to action. It is a good agency landing page and it is the
 * single most "not a trade catalogue" thing on the site, because it is the first thing a
 * visitor sees and it spends the whole first screen saying something rather than selling
 * anything. A trade catalogue's first screen is a way in.
 *
 * <h3>The h1 is here and it is small</h3>
 * The home page needs exactly one h1 and this block is the only thing above the fold, so
 * it carries it. It is set at body scale rather than display scale, which is what the
 * reference does too: the page's job is to route, and a banner headline competes with the
 * tiles for the attention that should be going to them. Small is not hidden — it is read
 * by a crawler and a screen reader exactly the same.
 *
 * <h3>Colour, since there are no photographs</h3>
 * The reference's tiles each carry a product shot. The catalogue has none, so the tiles
 * are typographic: one red, the rest dark and plain. That is a deliberate substitution,
 * not a placeholder — a tile with a grey box where a photograph should be reads as a page
 * that failed to load. `image` is on the interface so the shots drop in when they exist
 * without the layout changing.
 */
export function SolarPromoGrid({ title, tiles = [] }: SolarPromoGridProps) {
  if (!tiles.length) return null;

  return (
    <BlockRoot as="section" style={sectionStyle}>
      <div style={innerStyle}>
        {tx(title) ? <h1 style={titleStyle}>{tx(title)}</h1> : null}
        <ul style={gridStyle}>
          {tiles.map((tile, index) => (
            <li key={index} style={cellStyle(index)}>
              <PortalLink href={tile.href} style={tileStyle(tile.tone)}>
                {tile.image ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={tile.image} alt={tile.imageAlt ?? ''} style={imageStyle} />
                ) : null}
                <span style={tileBodyStyle}>
                  <span style={tileTitleStyle(index)}>{tx(tile.title)}</span>
                  {tx(tile.body) ? <span style={tileTextStyle}>{tx(tile.body)}</span> : null}
                  {tx(tile.ctaLabel) ? (
                    <span style={ctaStyle}>
                      {tx(tile.ctaLabel)} <span aria-hidden>→</span>
                    </span>
                  ) : null}
                </span>
              </PortalLink>
            </li>
          ))}
        </ul>
      </div>
    </BlockRoot>
  );
}

const sectionStyle: CSSProperties = {
  padding: '18px var(--space-6, 24px) 0',
  background: 'var(--color-surface-page, #fff)',
};

const innerStyle: CSSProperties = { maxWidth: 1180, margin: '0 auto' };

/** Body scale, not display scale. See the note on the class. */
const titleStyle: CSSProperties = {
  margin: '0 0 14px',
  fontSize: 15,
  fontWeight: 600,
  lineHeight: 1.3,
  color: 'var(--color-text-secondary, #475569)',
};

/**
 * The first tile spans two columns on a wide viewport, which is what stops four equal
 * rectangles reading as a row of buttons. `auto-fit` keeps it reflowing without a media
 * query, which inline styles cannot express.
 */
const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(260px, 100%), 1fr))',
  gap: 10,
  margin: 0,
  padding: 0,
  listStyle: 'none',
};

const cellStyle = (index: number): CSSProperties => ({
  display: 'flex',
  gridColumn: index === 0 ? 'span 2' : undefined,
  minWidth: 0,
});

const toneBackground = {
  brand: 'var(--color-brand-500, #c8102e)',
  dark: 'var(--color-text-primary, #0f1218)',
  plain: 'var(--color-surface-subtle, #f6f7f9)',
} as const;

const tileStyle = (tone: SolarPromoTile['tone'] = 'plain'): CSSProperties => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  justifyContent: 'flex-end',
  gap: 6,
  minHeight: 150,
  padding: '18px 20px',
  background: toneBackground[tone],
  color:
    tone === 'plain'
      ? 'var(--color-text-primary, #0f1218)'
      : 'var(--color-text-on-brand, #ffffff)',
  textDecoration: 'none',
  border: tone === 'plain' ? '1px solid var(--color-border-subtle, #e2e8f0)' : 'none',
});

const imageStyle: CSSProperties = {
  width: '100%',
  aspectRatio: '16 / 9',
  objectFit: 'cover',
  marginBottom: 8,
};

const tileBodyStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };

const tileTitleStyle = (index: number): CSSProperties => ({
  fontSize: index === 0 ? 'clamp(22px, 2.4vw, 30px)' : 'clamp(17px, 1.6vw, 20px)',
  fontWeight: 700,
  lineHeight: 1.12,
});

const tileTextStyle: CSSProperties = { fontSize: 14, lineHeight: 1.4, opacity: 0.88 };

const ctaStyle: CSSProperties = { marginTop: 4, fontSize: 14, fontWeight: 700 };
